import {Box, TextFieldProps} from '@mui/material';
import styles from '../../../styles/components/clientdetails/projectDrawerStyle.module.scss';
import {t} from 'i18next';
import SectionDetailsHeader from './SectionDetailsHeader';
import {ProjectFields} from '../../../types/project-type/projectDetailsType';
import {EditFlag} from './ProjectDrawer';
import {AurTextField} from '../../common/aur-text-field/AurTextField';
import {ChangeEvent, useState} from 'react';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import * as Yup from 'yup';
import _ from 'lodash';

const CHAR_LIMIT = 500;
const emailRegex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[\w-]{2,4}$/;

type OverseeingAttorneysProps = {
  attorneyNames: string;
  attorneyEmailAddresses: string;
  attorneyAdditionalContactInfo: string;
  isEdit?: boolean;
  saveChangesClickHandler: (projectFields: ProjectFields) => Promise<boolean>;
  editClickHandler: (editFlag: Partial<EditFlag>) => void;
  cancelClickHandler: (editFlag: Partial<EditFlag>) => void;
  hasEditProjectProfilePerm?: boolean;
};

type OverseeingAttorney = {
  attorneyNames: string;
  attorneyEmailAddresses: string;
  attorneyAdditionalContactInfo: string;
};

//validation schema for Overseeing attorney object
const overseeingAttorneySchema = Yup.object<OverseeingAttorney>().shape({
  attorneyNames: Yup.string()
    .default('')
    .test(
      'valid-names',
      t('clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'),
      function (value: string) {
        const isValueLengthValid = value.length < CHAR_LIMIT + 1;

        return isValueLengthValid
          ? true
          : this.createError({
              message: t(
                'clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'
              ),
            });
      }
    ),
  attorneyEmailAddresses: Yup.string()
    .default('')
    .max(
      CHAR_LIMIT,
      t('clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText')
    )
    .test(
      'is-valid-emails',
      t('clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'),
      function (value) {
        if (!value) return true;

        if (value.length > CHAR_LIMIT)
          return this.createError({
            message: t(
              'clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'
            ),
          });

        const emails = value.split(',').map((email) => email.trim());
        const isValidEmails = emails.every((email) => emailRegex.test(email));

        return isValidEmails
          ? isValidEmails
          : this.createError({
              message: t(
                'clientDetailsPage.projectDrawer.invalidEmailErrorText'
              ),
            });
      }
    ),
  attorneyAdditionalContactInfo: Yup.string()
    .default('')
    .test(
      'valid-additional-contact-info',
      t('clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'),
      function (value: string) {
        const isValueLengthValid = value.length < CHAR_LIMIT + 1;

        return isValueLengthValid
          ? true
          : this.createError({
              message: t(
                'clientDetailsPage.projectDrawer.CharLimitExceedErrorHelperText'
              ),
            });
      }
    ),
});

const ProjectOverseeingAttorneys: React.FC<OverseeingAttorneysProps> = ({
  attorneyNames,
  attorneyEmailAddresses: attorneyContactInfo,
  isEdit = false,
  hasEditProjectProfilePerm,
  attorneyAdditionalContactInfo: additionalContactInfo,
  saveChangesClickHandler,
  editClickHandler,
  cancelClickHandler,
}) => {
  const {showModal} = useModal();

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [overseeingAttorney, setOverseeingAttorney] =
    useState<OverseeingAttorney>({
      attorneyNames: attorneyNames,
      attorneyEmailAddresses: attorneyContactInfo,
      attorneyAdditionalContactInfo: additionalContactInfo,
    });

  //capturing the initial state to track state mutation
  const [overseeingAttorneyInitialState, setOverseeingAttorneyInitialState] =
    useState<OverseeingAttorney>({
      attorneyNames: attorneyNames,
      attorneyEmailAddresses: attorneyContactInfo,
      attorneyAdditionalContactInfo: additionalContactInfo,
    });

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);

  const isValidInput = (inputVal: string) => {
    return inputVal.length <= CHAR_LIMIT;
  };

  const validateInput = (overseeingAttorneyDetails: OverseeingAttorney) => {
    overseeingAttorneySchema
      .validate(overseeingAttorneyDetails, {abortEarly: false})
      .then((value) => {
        const isStateMutated = !_.isEqual(
          overseeingAttorneyInitialState,
          value
        );

        setSaveButtonDisabled(!isStateMutated);

        setErrors((prevErrors) => {
          const updatedErrors = {...prevErrors};
          const allFields = Object.keys(overseeingAttorneyDetails);

          allFields.forEach((field) => {
            delete updatedErrors[field];
          });

          return updatedErrors;
        });
      })
      .catch((err) => {
        setSaveButtonDisabled(true);

        setErrors((prevErr) => {
          const updatedError = {...prevErr};

          err.inner.forEach((e: Yup.ValidationError) => {
            const errKey = e.path!;
            const errMessage = e.message;
            updatedError[errKey] = errMessage;
          });

          const allFields = Object.keys(overseeingAttorneyDetails);

          allFields.forEach((field) => {
            const hasError = err.inner.some(
              (e: Yup.ValidationError) => e.path === field
            );
            if (!hasError) {
              delete updatedError[field];
            }
          });

          return updatedError;
        });
      });
  };

  const onOverseeingAttorneysDetailsChange = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const {value, name} = e.target;
    let updatedData: OverseeingAttorney;
    setOverseeingAttorney((curr) => {
      updatedData = {...curr, [name]: value.trimStart()};
      validateInput(updatedData);
      return updatedData;
    });
  };

  const hasLocalChanges = () => {
    if (!_.isEqual(overseeingAttorney, overseeingAttorneyInitialState)) {
      return true;
    } else {
      return false;
    }
  };

  const onEditClickHandler = () => {
    editClickHandler({overseeingAttorneys: true});
  };

  const handleCancelClick = () => {
    setOverseeingAttorney({
      attorneyNames: attorneyNames,
      attorneyEmailAddresses: attorneyContactInfo,
      attorneyAdditionalContactInfo: additionalContactInfo,
    });
    cancelClickHandler({clientInformation: false});
  };

  const onCancelClickHandler = () => {
    if (hasLocalChanges()) {
      showModal({
        title: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.title'
        ),
        description: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.description'
        ),
        labelOkButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.okButtonLabel'
        ),
        labelCancelButton: t(
          'clientDetailsPage.projectDrawer.modal.closePanelUnsavedChangePopup.cancelButtonLabel'
        ),
        onOk: () => {
          handleCancelClick();
        },
      });
    } else {
      handleCancelClick();
    }
  };

  const onSaveChangeHandler = async () => {
    if (
      isValidInput(overseeingAttorney.attorneyNames) &&
      isValidInput(overseeingAttorney.attorneyEmailAddresses) &&
      isValidInput(overseeingAttorney.attorneyAdditionalContactInfo)
    ) {
      const isSavedSuccessfully = await saveChangesClickHandler({
        attorneysName: overseeingAttorney.attorneyNames.trimEnd(),
        attorneysEmail: overseeingAttorney.attorneyEmailAddresses.trimEnd(),
        attorneysAdditionalContactInfo:
          overseeingAttorney.attorneyAdditionalContactInfo.trimEnd(),
      });

      //updates the initial state once the data is updated in the backend
      setOverseeingAttorneyInitialState(overseeingAttorney);

      setSaveButtonDisabled(isSavedSuccessfully);
    }
  };

  const viewMode = (
    <Box className={`${styles.projectDetailsOverseeingAttorneyBody}`}>
      <Box className={`${styles.overseeingAttorneyColumnFlex}`}>
        <Box className={`${styles.columnHeader}`}>
          {t('clientDetailsPage.projectDrawer.overseeingAttorneyNames')}
        </Box>
        <Box className={`${styles.clientInfoColumnValue}`}>{attorneyNames}</Box>
      </Box>
      <Box className={`${styles.overseeingAttorneyColumnFlex}`}>
        <Box className={`${styles.columnHeader}`}>
          {t(
            'clientDetailsPage.projectDrawer.overseeingAttorneyEmailAddresses'
          )}
        </Box>
        <Box className={`${styles.clientInfoColumnValue}`}>
          {attorneyContactInfo}
        </Box>
      </Box>
      <Box className={`${styles.overseeingAttorneyColumnFlex}`}>
        <Box className={`${styles.columnHeader}`}>
          {t('clientDetailsPage.projectDrawer.additionalContactInfo')}
        </Box>
        <Box className={`${styles.clientInfoColumnValue}`}>
          {additionalContactInfo}
        </Box>
      </Box>
    </Box>
  );

  const editMode = () => {
    const sharedFieldProps: TextFieldProps = {
      variant: 'filled',
      onChange: onOverseeingAttorneysDetailsChange,
      multiline: true,
      fullWidth: true,
    };

    const uniqueFieldProps = [
      {
        error: !_.isEmpty(errors.attorneyNames),
        label: t('clientDetailsPage.projectDrawer.overseeingAttorneyNames'),
        value: overseeingAttorney.attorneyNames,
        helperText: errors.attorneyNames
          ? errors.attorneyNames
          : t('clientDetailsPage.projectDrawer.charLimitValidHelperText'),
        name: 'attorneyNames',
      },
      {
        error: !_.isEmpty(errors.attorneyEmailAddresses),
        label: t(
          'clientDetailsPage.projectDrawer.overseeingAttorneyEmailAddresses'
        ),
        value: overseeingAttorney.attorneyEmailAddresses,
        helperText: errors.attorneyEmailAddresses
          ? errors.attorneyEmailAddresses
          : t(
              'clientDetailsPage.projectDrawer.overseeingAttorneyContactInfoValidHelperText'
            ),
        name: 'attorneyEmailAddresses',
      },
      {
        error: !_.isEmpty(errors.attorneyAdditionalContactInfo),
        label: t('clientDetailsPage.projectDrawer.additionalContactInfo'),
        value: overseeingAttorney.attorneyAdditionalContactInfo,
        helperText: errors.attorneyAdditionalContactInfo
          ? errors.attorneyAdditionalContactInfo
          : t('clientDetailsPage.projectDrawer.charLimitValidHelperText'),
        name: 'attorneyAdditionalContactInfo',
      },
    ];

    const updatedFieldProps = uniqueFieldProps.map((field) => ({
      ...field,
      ...sharedFieldProps,
      textFieldContainerClassName: `${styles.projectOverseeingAttorneysTextField}`,
      textFieldClassName: `aur-textfield custom-text-field resizableTextField  ${styles.fieldFocused} ${styles.inputFieldTypography}`,
    }));

    return (
      <Box className={`${styles.projectDrawerSectionEditContainer} `}>
        {updatedFieldProps.map((field, idx) => (
          <Box className={field.textFieldContainerClassName}>
            <AurTextField
              key={`${field.name}-${idx}`}
              ariaLabel={`${field.name}-${idx}`}
              className={field.textFieldClassName}
              {...field}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box className={`${styles.projectDetailsClientInfoContainer}`}>
      <Box className={`${styles.projectDetailsClientInfoHeader}`}>
        <SectionDetailsHeader
          headerText={t('clientDetailsPage.projectDrawer.overseeingAttorneys')}
          onCancelHandler={onCancelClickHandler}
          onEditHandler={onEditClickHandler}
          onSaveChangesHandler={onSaveChangeHandler}
          isEdit={isEdit}
          isEditButtonVisible={hasEditProjectProfilePerm}
          isSaveButtonDisabled={saveButtonDisabled}
        />
      </Box>

      {isEdit ? editMode() : viewMode}
    </Box>
  );
};

export default ProjectOverseeingAttorneys;
